/**
 * The beacon (`ProductOverview.md` §4.5).
 *
 * At the handoff range the product stops navigating and starts helping you be *seen*: one tap
 * strobes the whole screen in bright amber so a raised phone can be picked out across a dark
 * festival crowd. It is a torch, not a decoration.
 *
 * It also asks for the screen wake lock, because a phone that sleeps mid-strobe is useless, and
 * it turns itself off after a couple of minutes so it cannot quietly flatten a battery.
 */

import { el } from '../lib/dom';

/** Long enough to find someone, short enough not to be forgotten about. */
const AUTO_STOP_MS = 2 * 60 * 1000;

export interface Beacon {
  element: HTMLElement;
  readonly active: boolean;
  toggle: () => void;
  stop: () => void;
  destroy: () => void;
}

type WakeLock = { release: () => Promise<void> };

export function createBeacon(onChange?: (active: boolean) => void): Beacon {
  const element = el('div', {
    class: 'beacon',
    hidden: true,
    'aria-hidden': 'true',
    // Tapping anywhere on the strobe turns it off — nobody hunts for a button at this point.
    onclick: () => beacon.stop(),
  });

  let active = false;
  let autoStop: number | null = null;
  let wakeLock: WakeLock | null = null;

  const acquireWakeLock = async (): Promise<void> => {
    try {
      const lock = navigator.wakeLock as
        { request: (type: 'screen') => Promise<WakeLock> } | undefined;
      wakeLock = (await lock?.request('screen')) ?? null;
    } catch {
      // Unsupported or refused. The strobe still works; the screen may just time out.
    }
  };

  const releaseWakeLock = (): void => {
    void wakeLock?.release().catch(() => {});
    wakeLock = null;
  };

  const beacon: Beacon = {
    element,
    get active() {
      return active;
    },
    toggle: () => {
      if (active) beacon.stop();
      else {
        active = true;
        element.hidden = false;
        element.setAttribute('aria-hidden', 'false');
        void acquireWakeLock();
        autoStop = window.setTimeout(() => beacon.stop(), AUTO_STOP_MS);
        onChange?.(true);
      }
    },
    stop: () => {
      if (!active) return;
      active = false;
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      if (autoStop !== null) window.clearTimeout(autoStop);
      autoStop = null;
      releaseWakeLock();
      onChange?.(false);
    },
    destroy: () => {
      beacon.stop();
      element.remove();
    },
  };

  return beacon;
}
